// Image Generation — chat-style surface (I3 Web, Owner 14-10).
//
// UX contract (Owner directive, ChatGPT-style reference):
//   * opening «Генерация изображений» shows a chat thread;
//   * the composer at the bottom carries the prompt + preset settings;
//   * each user prompt renders as a right-side bubble; the result renders
//     as a left-side image card under it;
//   * while queued/running the card shows a generation animation;
//   * under a succeeded image: download / copy / share / regenerate;
//   * cancel is available while queued/running.
// State authority: the backend only. Canonical states are rendered as-is;
// no fake queue math, no provider access, no concurrency control here.
import { useCallback, useEffect, useRef, useState } from "react";
import {
  X,
  Sparkles,
  Download,
  RefreshCw,
  Ban,
  Copy,
  Share,
  Loader2,
  AlertTriangle,
  Check,
  Image as ImageIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ApiError } from "@/lib/api";
import {
  cancelGeneration,
  createGeneration,
  downloadGenerationImage,
  fetchGeneration,
  fetchGenerationImageBlob,
  fetchGenerationImageBlobUrl,
  fetchImageGenCapability,
  listGenerations,
  ImageGenCapability,
  ImageGenJob,
  PRESET_LABELS,
  STATUS_LABELS,
} from "@/lib/imagegen/api";

const POLL_MS = 3000;

function statusClasses(status: ImageGenJob["status"]): string {
  switch (status) {
    case "failed":
    case "expired":
      return "bg-red-500/15 text-red-400";
    case "cancelled":
      return "bg-hoverbg2 text-tertiary";
    default:
      return "bg-hoverbg2 text-secondary";
  }
}

export function ImagePanel({ onClose }: { onClose: () => void }) {
  const [capability, setCapability] = useState<ImageGenCapability | null | undefined>(undefined);
  const [jobs, setJobs] = useState<ImageGenJob[]>([]);
  const [prompt, setPrompt] = useState("");
  const [preset, setPreset] = useState("square_768");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ jobId: string; url: string } | null>(null);
  // Per-job object URLs for inline chat rendering (revoked on unmount).
  const [imgUrls, setImgUrls] = useState<Record<string, string>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const urlsRef = useRef<Record<string, string>>({});
  const threadRef = useRef<HTMLDivElement | null>(null);

  const putUrl = useCallback((jobId: string, url: string) => {
    urlsRef.current[jobId] = url;
    setImgUrls((prev) => ({ ...prev, [jobId]: url }));
  }, []);

  // Capability probe (feature gate off -> null -> disabled state).
  useEffect(() => {
    fetchImageGenCapability()
      .then(setCapability)
      .catch(() => setCapability(null));
  }, []);

  const loadJobs = useCallback(() => {
    listGenerations(30)
      .then((next) => setJobs(next))
      .catch(() => {
        /* list failure is non-fatal; keep the last known state */
      });
  }, []);

  useEffect(() => {
    if (capability?.entitled) loadJobs();
  }, [capability, loadJobs]);

  // Poll while any job is non-terminal (backend is the authority).
  useEffect(() => {
    const active = jobs.filter((j) => j.status === "queued" || j.status === "running");
    if (active.length === 0) return;
    const timer = window.setInterval(() => {
      active.forEach((j) => {
        fetchGeneration(j.id)
          .then((fresh) => setJobs((prev) => prev.map((x) => (x.id === fresh.id ? fresh : x))))
          .catch(() => {
            /* transient poll failure — next tick retries */
          });
      });
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [jobs]);

  // Load image bytes for succeeded jobs (authorized blob route).
  useEffect(() => {
    jobs
      .filter((j) => j.status === "succeeded" && j.has_result && !urlsRef.current[j.id])
      .forEach((j) => {
        fetchGenerationImageBlobUrl(j.id)
          .then((url) => putUrl(j.id, url))
          .catch(() => undefined);
      });
  }, [jobs, putUrl]);

  // Revoke all object URLs on unmount.
  useEffect(() => {
    return () => {
      Object.values(urlsRef.current).forEach((u) => URL.revokeObjectURL(u));
      urlsRef.current = {};
    };
  }, []);

  // Keep the thread pinned to the newest message.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [jobs, imgUrls]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2500);
  };

  const submit = async () => {
    const text = prompt.trim();
    if (!text) {
      setFormError("Введите описание изображения.");
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      const job = await createGeneration({ prompt: text, preset });
      setJobs((prev) => [...prev, job]);
      setPrompt("");
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Не удалось создать задание");
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async (job: ImageGenJob) => {
    setFormError(null);
    try {
      const fresh = await createGeneration({
        prompt: job.prompt,
        preset: job.preset,
        seed: job.seed ?? undefined,
      });
      setJobs((prev) => [...prev, fresh]);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Не удалось повторить генерацию");
    }
  };

  const doCancel = async (job: ImageGenJob) => {
    try {
      const fresh = await cancelGeneration(job.id);
      setJobs((prev) => prev.map((x) => (x.id === fresh.id ? fresh : x)));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // Race with completion — backend state wins: re-read the job.
        fetchGeneration(job.id)
          .then((fresh) => setJobs((prev) => prev.map((x) => (x.id === fresh.id ? fresh : x))))
          .catch(() => undefined);
      }
    }
  };

  const doDownload = async (job: ImageGenJob) => {
    try {
      await downloadGenerationImage(job.id, `realityai-${job.id.slice(0, 8)}.png`);
    } catch {
      showToast("Не удалось скачать изображение.");
    }
  };

  const doCopy = async (job: ImageGenJob) => {
    try {
      const blob = await fetchGenerationImageBlob(job.id);
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
      setCopiedId(job.id);
      window.setTimeout(() => setCopiedId(null), 2000);
    } catch {
      showToast("Не удалось скопировать изображение.");
    }
  };

  const doShare = async (job: ImageGenJob) => {
    try {
      const blob = await fetchGenerationImageBlob(job.id);
      const file = new File([blob], `realityai-${job.id.slice(0, 8)}.png`, { type: blob.type });
      const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
      if (nav.canShare?.({ files: [file] }) && nav.share) {
        await nav.share({ files: [file] });
      } else {
        // Fallback: download (the OS share sheet is unavailable here).
        await doDownload(job);
      }
    } catch (e) {
      if ((e as Error)?.name !== "AbortError") showToast("Не удалось поделиться изображением.");
    }
  };

  const openPreview = (job: ImageGenJob) => {
    const url = urlsRef.current[job.id];
    if (url) setPreview({ jobId: job.id, url });
  };

  const featureOff = capability === null;
  const notEntitled = capability && capability.entitled === false;

  const renderJob = (job: ImageGenJob) => {
    const active = job.status === "queued" || job.status === "running";
    const url = imgUrls[job.id];
    return (
      <div key={job.id} className="flex flex-col gap-2" data-testid={`job-${job.status}`} data-job-id={job.id}>
        {/* user prompt bubble (right) */}
        <div className="flex justify-end">
          <div className="max-w-[80%] rounded-2xl rounded-br-md bg-accent/15 border border-accent/25 px-4 py-2.5 text-sm text-primary whitespace-pre-wrap break-words">
            {job.prompt}
          </div>
        </div>

        {/* result card (left) */}
        <div className="flex justify-start">
          <div className="max-w-[85%] w-full max-w-md">
            <div className="relative rounded-2xl border border-hairline bg-surface overflow-hidden">
              {job.status === "succeeded" && url && (
                <button
                  onClick={() => openPreview(job)}
                  className="block w-full cursor-zoom-in"
                  aria-label="Превью"
                  data-testid="job-image"
                >
                  <img
                    src={url}
                    alt={job.prompt}
                    className="w-full object-contain max-h-[420px]"
                  />
                </button>
              )}

              {active && (
                <div className="aspect-square max-h-[320px] w-full flex flex-col items-center justify-center gap-3 imggen-generating">
                  <div className="imggen-orb" aria-hidden />
                  <div className="flex items-center gap-2 text-sm text-secondary">
                    <Loader2 size={14} className="animate-spin" />
                    {STATUS_LABELS[job.status]}
                  </div>
                  <div className="text-[11px] text-tertiary">
                    {PRESET_LABELS[job.preset] || job.preset} · {job.requested_output.width}×
                    {job.requested_output.height}
                  </div>
                </div>
              )}

              {(job.status === "failed" || job.status === "expired" || job.status === "cancelled") && (
                <div className="px-4 py-5 flex items-start gap-2.5">
                  <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
                  <div className="text-sm text-secondary">
                    <span className={cn("inline-block px-2 py-0.5 rounded-md text-[11px] font-medium mb-1", statusClasses(job.status))}>
                      {STATUS_LABELS[job.status]}
                    </span>
                    {job.error?.message && <div className="mt-1">{job.error.message}</div>}
                  </div>
                </div>
              )}
            </div>

            {/* action row under the image */}
            <div className="mt-1.5 flex items-center gap-1">
              {job.status === "succeeded" && (
                <>
                  <button
                    onClick={() => doDownload(job)}
                    className="p-2 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg2 transition-colors"
                    aria-label="Скачать"
                    title="Скачать"
                  >
                    <Download size={15} />
                  </button>
                  <button
                    onClick={() => doCopy(job)}
                    className="p-2 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg2 transition-colors"
                    aria-label="Скопировать"
                    title="Скопировать"
                  >
                    {copiedId === job.id ? <Check size={15} className="text-accent" /> : <Copy size={15} />}
                  </button>
                  <button
                    onClick={() => doShare(job)}
                    className="p-2 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg2 transition-colors"
                    aria-label="Поделиться"
                    title="Поделиться"
                  >
                    <Share size={15} />
                  </button>
                </>
              )}
              {active && (
                <button
                  onClick={() => doCancel(job)}
                  data-testid={`cancel-${job.id}`}
                  className="p-2 rounded-lg text-secondary hover:text-red-400 hover:bg-hoverbg2 transition-colors"
                  aria-label="Отменить"
                  title="Отменить"
                >
                  <Ban size={15} />
                </button>
              )}
              {job.status !== "queued" && job.status !== "running" && (
                <button
                  onClick={() => regenerate(job)}
                  data-testid={`regen-${job.id}`}
                  className="p-2 rounded-lg text-secondary hover:text-accent hover:bg-hoverbg2 transition-colors"
                  aria-label="Повторить"
                  title="Повторить генерацию"
                >
                  <RefreshCw size={15} />
                </button>
              )}
              <div className="ml-1 text-[11px] text-tertiary">
                {PRESET_LABELS[job.preset] || job.preset}
                {job.duration_seconds != null ? ` · ${job.duration_seconds.toFixed(1)} с` : ""}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center md:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div
        data-testid="image-panel"
        className="relative flex flex-col w-full h-full md:max-w-3xl md:max-h-[92vh] md:h-[92vh] md:rounded-2xl border border-strongline bg-app shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-hairline shrink-0">
          <div className="flex items-center gap-2.5">
            <Sparkles size={18} className="text-accent" strokeWidth={1.8} />
            <h2 className="text-base font-semibold text-primary">Генерация изображений</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-tertiary hover:text-primary hover:bg-hoverbg2 transition-colors"
            aria-label="Закрыть"
          >
            <X size={18} />
          </button>
        </div>

        {capability === undefined && (
          <div className="flex-1 py-10 flex items-center justify-center gap-2 text-secondary text-sm">
            <Loader2 size={16} className="animate-spin" /> Проверка доступности…
          </div>
        )}

        {featureOff && (
          <div className="flex-1 py-10 text-center text-sm text-tertiary" data-testid="imggen-disabled">
            Генерация изображений сейчас недоступна.
          </div>
        )}

        {notEntitled && (
          <div className="flex-1 py-10 text-center text-sm text-tertiary" data-testid="imggen-not-entitled">
            Генерация изображений недоступна на вашем тарифе.
          </div>
        )}

        {capability?.entitled && (
          <>
            {/* chat thread */}
            <div ref={threadRef} className="flex-1 min-h-0 overflow-y-auto px-5 py-4" data-testid="imggen-thread">
              {jobs.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center gap-3 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center">
                    <ImageIcon size={26} className="text-accent" strokeWidth={1.8} />
                  </div>
                  <div className="text-sm text-secondary">
                    Опишите изображение — оно появится здесь.
                  </div>
                  <div className="text-[11px] text-tertiary">
                    Готовые изображения также сохраняются в Библиотеку.
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-5">{jobs.map(renderJob)}</div>
              )}
            </div>

            {/* composer with settings */}
            <div className="shrink-0 border-t border-hairline px-4 py-3">
              {/* Daily quota line (Owner 15-10). Admin/staff: «Без ограничений»
                  — never a fake number. Tariff users: «Осталось N из M сегодня». */}
              <div className="mb-2 text-[11px] text-tertiary" data-testid="imggen-quota-line">
                {capability.unlimited
                  ? "Без ограничений"
                  : capability.remaining_today === 0
                    ? "Генерации на сегодня закончились."
                    : capability.remaining_today != null && capability.daily_limit != null
                      ? `Осталось ${capability.remaining_today} из ${capability.daily_limit} сегодня`
                      : ""}
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mb-2">
                {(capability.presets.length
                  ? capability.presets
                  : Object.keys(PRESET_LABELS).map((code) => ({
                      code,
                      output_width: 0,
                      output_height: 0,
                      upscale: false,
                    }))
                ).map((p) => (
                  <button
                    key={p.code}
                    onClick={() => setPreset(p.code)}
                    data-testid={`preset-${p.code}`}
                    className={cn(
                      "px-2.5 py-1 rounded-lg text-[11px] border transition-colors",
                      preset === p.code
                        ? "border-accent/60 bg-accent/15 text-accent"
                        : "border-strongline bg-surface text-secondary hover:text-primary"
                    )}
                  >
                    {PRESET_LABELS[p.code] || p.code}
                    {p.upscale ? " · upscale" : ""}
                  </button>
                ))}
              </div>
              <div className="flex items-end gap-2">
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void submit();
                    }
                  }}
                  placeholder="Опишите изображение…"
                  maxLength={2000}
                  rows={1}
                  data-testid="imggen-prompt"
                  className="flex-1 rounded-xl border border-strongline bg-surface px-3.5 py-2.5 text-sm text-primary placeholder:text-tertiary focus:outline-none focus:border-accent/60 resize-none max-h-32"
                />
                <button
                  onClick={submit}
                  disabled={busy}
                  data-testid="imggen-generate"
                  className="h-10 w-10 rounded-xl bg-accent text-black hover:opacity-90 transition-opacity flex items-center justify-center shrink-0 disabled:opacity-50"
                  aria-label="Сгенерировать"
                  title="Сгенерировать"
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                </button>
              </div>
              {formError && (
                <div className="mt-2 text-xs text-red-400" data-testid="imggen-form-error">
                  {formError}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[120] px-4 py-2 rounded-xl bg-elevated border border-strongline text-sm text-primary shadow-2xl" data-testid="imggen-toast">
          {toast}
        </div>
      )}

      {/* full-size preview (in-palette modal) */}
      {preview && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70"
          onClick={() => setPreview(null)}
        >
          <div
            className="relative max-w-2xl w-full mx-4 rounded-xl border border-strongline bg-elevated p-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-sm text-primary">Результат генерации</span>
              <button
                onClick={() => setPreview(null)}
                className="p-1 rounded text-tertiary hover:text-primary transition-colors"
                aria-label="Закрыть превью"
              >
                <X size={16} />
              </button>
            </div>
            <img
              src={preview.url}
              alt="Результат генерации"
              data-testid="imggen-preview-img"
              className="w-full max-h-[70vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
}
